import type { RootState } from "../state";
import message from "@/utils/message/messageFactory";
import {
  findVoiceProfileListByUseId,
  getCategoryCountVoiceProfileListNum
} from "@/api/audio";
import { $t } from "@/plugins/i18n";

// 音色相关方法

export async function fetchInitialVoiceData(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  console.log("🔍 开始获取音色数据...");
  const res = await getCategoryCountVoiceProfileListNum({ voiceName: "" });
  if (res?.success && Array.isArray(res.result)) {
    state.voiceLangs = res.result;
    console.log("🔍 设置音色语言列表:", state.voiceLangs);

    let foundLanguageIdx = 0;
    state.voiceLangs.forEach((item, index) => {
      if (item.language == "zh") {
        foundLanguageIdx = index;
      }
    });
    if (state.productList.language) {
      let languageIndex = state.voiceLangs.findIndex(
        lang =>
          lang.language === state.productList.language ||
          lang.languageZH === state.productList.languageZh
      );
      if (languageIndex < 0) {
        languageIndex = state.voiceLangs.findIndex(
          lang =>
            lang.language.toLowerCase() ===
            state.productList.language.toLowerCase()
        );
      }
      if (languageIndex >= 0) foundLanguageIdx = languageIndex;
    }

    state.activeLanguageIdx = foundLanguageIdx;
    if (state.voiceLangs[foundLanguageIdx]) {
      state.voiceLang = foundLanguageIdx.toString();
    } else {
      state.voiceLang = "0";
    }

    if (state.productList.voiceProfileCode || state.productList.voiceId) {
      console.log(
        "🔍 产品有配置音色，查找并设置:",
        state.productList.voiceProfileCode,
        state.productList.voiceId
      );
      await this.findAndSetVoice();
    } else {
      console.log("🔍 产品无配置音色，使用默认男声");
      state.voiceGender = "male";
      await this.fetchVoiceList();
    }
  } else {
    console.error("🔍 音色语言接口失败:", res);
  }
}

export async function findAndSetVoice(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  const tempGender = state.voiceGender;

  const findInGender = async gender => {
    state.voiceGender = gender;
    await this.fetchVoiceList();
    return state.voiceList.find(
      voice =>
        voice.voiceCode === state.productList.voiceProfileCode ||
        voice.id === state.productList.voiceId
    );
  };

  let foundVoice = await findInGender("male");
  if (!foundVoice) {
    foundVoice = await findInGender("female");
  }

  if (foundVoice) {
    // 找到了匹配的音色，设置选中状态
    state.selectedVoiceId = foundVoice.id;
    state.selectedVoice = foundVoice;

    const voiceIndex = state.voiceList.findIndex(v => v.id === foundVoice.id);
    if (voiceIndex >= 0) {
      state.currentVoicePage = Math.floor(voiceIndex / state.voicePageSize) + 1;
    }
  } else {
    // 当前语言没有找到匹配的音色，尝试查找自定义音色
    state.activeLanguageIdx = 0;
    let foundVoice = await findInGender("male");
    if (!foundVoice) {
      foundVoice = await findInGender("female");
    }
    if (foundVoice) {
      state.selectedVoiceId = foundVoice.id;
      state.selectedVoice = foundVoice;
      state.voiceLang = state.activeLanguageIdx.toString();
      const voiceIndex = state.voiceList.findIndex(v => v.id === foundVoice.id);
      if (voiceIndex >= 0) {
        state.currentVoicePage =
          Math.floor(voiceIndex / state.voicePageSize) + 1;
      }
      return;
    }
    // 没有找到匹配的音色，说明配置的音色可能已被删除
    console.warn(
      `配置的音色已被删除: voiceProfileCode=${state.productList.voiceProfileCode}, voiceId=${state.productList.voiceId}`
    );

    // 清空已删除的音色配置
    if (state.productList.voiceProfileCode) {
      state.productList.voiceProfileCode = null;
    }
    if (state.productList.voiceId) {
      state.productList.voiceId = null;
    }

    // 恢复原来的性别设置并重新获取音色列表
    if (state.voiceGender !== tempGender) {
      state.voiceGender = tempGender;
      state.activeLanguageIdx = Number(state.voiceLang);
      await this.fetchVoiceList();
    }

    // 默认选择第一个可用的音色
    if (state.voiceList && state.voiceList.length > 0) {
      const defaultVoice = state.voiceList[0];
      state.selectedVoiceId = defaultVoice.id;
      state.selectedVoice = defaultVoice;
      state.currentVoicePage = 1;
      console.log(
        `已自动选择默认音色: ${defaultVoice.voiceName || defaultVoice.name}`
      );
    } else {
      // 如果连默认音色都没有，清空选中状态
      state.selectedVoiceId = null;
      state.selectedVoice = null;
    }
  }
}

export async function fetchVoiceList(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  const lang = state.voiceLangs[state.activeLanguageIdx];
  console.log("🔍 获取音色列表 - 当前语言:", lang, "性别:", state.voiceGender);

  if (!lang) {
    console.warn("🔍 没有找到当前语言，清空音色列表");
    state.voiceList = [];
    return;
  }

  const res = await findVoiceProfileListByUseId({
    language: lang.language,
    gender: state.voiceGender,
    voiceName: state.voiceName || null
  });

  if (res?.success && Array.isArray(res.result)) {
    state.voiceList = res.result;
    state.currentVoicePage = 1;

    console.log("🔍 设置音色列表成功，数量:", res.result.length);

    // 数据加载完成后，检查是否有选中的音色，如果有则自动跳转到对应页码
    if (state.selectedVoice && state.selectedVoice.voiceCode) {
      this.syncVoiceToPage();
    }
  } else {
    state.voiceList = [];
    console.error("🔍 音色列表接口失败或无数据");
  }
}

export async function fetchLanguageList(
  this: RootState & { [key: string]: any }
) {
  const res = await getCategoryCountVoiceProfileListNum({
    voiceName: this.currentRoomState.voiceName || null
  });
  if (res?.success && Array.isArray(res.result)) {
    this.currentRoomState.voiceLangs = res.result;
    if (res.result.length > 0) {
      this.currentRoomState.activeLanguageIdx = 0;
      this.currentRoomState.voiceLang = "0";
      if (
        (res.result[0].Male || res.result[0].Femal) &&
        this.currentRoomState.voiceName
      ) {
        this.currentRoomState.voiceGender = res.result[0].Male
          ? "male"
          : "female";
      }
    }
  } else {
    this.currentRoomState.voiceLangs = [];
    this.currentRoomState.voiceLang = "0";
  }
}

/**
 * 同步选中的音色到对应的页码
 * 此函数用于在音色列表加载完成后，自动跳转到选中音色所在的页码
 */
export function syncVoiceToPage(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  // 确保音色列表和选中音色都存在
  if (!state.voiceList || !state.voiceList.length || !state.selectedVoice) {
    return;
  }

  // 查找选中音色在列表中的索引
  const voiceIndex = state.voiceList.findIndex(
    v => v.voiceCode === state.selectedVoice.voiceCode
  );

  if (voiceIndex !== -1) {
    const targetPage = Math.floor(voiceIndex / state.voicePageSize) + 1;

    // 如果目标页码与当前页码不同，则跳转到目标页码
    if (targetPage !== state.currentVoicePage) {
      this.handleVoicePageChange(targetPage);
    }
  }
}

export async function onGenderTabClick(
  this: RootState & { [key: string]: any },
  gender: string
) {
  if (!this.currentRoomState) return;
  this.currentRoomState.voiceGender = gender;
  await this.fetchVoiceList();
}

export async function handleSearchVoice(
  this: RootState & { [key: string]: any },
  voice: string
) {
  if (!this.currentRoomState) return;
  console.log(`🔍 搜索语音: ${voice}`);
  this.currentRoomState.voiceName = voice;
  await this.fetchLanguageList();
  await this.fetchVoiceList();
}

export async function handleChangeLaugsTabs(
  this: RootState & { [key: string]: any },
  langIndex: number
) {
  if (!this.currentRoomState) return;
  this.currentRoomState.activeLanguageIdx = langIndex;
  this.currentRoomState.voiceLang = langIndex.toString();
  // 重置页码为1，确保切换标签页时从第一页开始显示
  this.currentRoomState.currentVoicePage = 1;
  await this.fetchVoiceList();
}

export function selectVoice(
  this: RootState & { [key: string]: any },
  voice: any
) {
  if (!this.currentRoomState) return;
  if (this.currentRoomState.productList.status === "live") {
    return message.error($t("msg.cannotSwitchVoiceWhileLive"), true);
  }

  // 设置选中的音色
  this.currentRoomState.selectedVoiceId = voice.id;
  this.currentRoomState.selectedVoice = voice;

  // 只有当音色列表已经有数据时，才尝试同步页码
  // 如果列表为空，等待fetchVoiceList完成后会自动调用syncVoiceToPage进行同步
  if (
    this.currentRoomState.voiceList &&
    this.currentRoomState.voiceList.length > 0
  ) {
    this.syncVoiceToPage();
  }
}

/**
 *
 * @param this
 * @param  tabIndex 此时改在那个tab栏
 */
export function setVoicelang(
  this: RootState & { [key: string]: any },
  tabIndex: number
) {
  if (this.currentRoomState) this.currentRoomState.voiceLang = tabIndex;
}

export function handleVoicePageChange(
  this: RootState & { [key: string]: any },
  page: number
) {
  if (this.currentRoomState) this.currentRoomState.currentVoicePage = page;
}
